import React, { useState, useEffect } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";

import {
  Modal,
  Button,
  TextField,
  Switch,
  Box,
  Card,
  CardContent,
  Typography,
} from "@mui/material";
import {
  Collections,
  FirstOption,
  OnboardingAcquisition,
  SecondOption,
} from "../types/type";
import { uploadFileToStorage } from "../api/storage/storage.api";
import { OnboardingAddModal } from "./OnboardingAddModal";

type ContentType = "first" | "second";
type ContentOption = FirstOption | SecondOption;

const isFirstOption = (option: ContentOption): option is FirstOption => {
  return "imageUrl" in option;
};

const isSecondOption = (option: ContentOption): option is SecondOption => {
  return "thumbnailUrl" in option;
};

interface EditModalProps {
  open: boolean;
  onClose: () => void;
  data: ContentOption;
  onSave: (updatedData: ContentOption) => void;
  type: ContentType;
}

const EditModal: React.FC<EditModalProps> = ({
  open,
  onClose,
  data,
  onSave,
  type,
}) => {
  const [editData, setEditData] = useState<ContentOption>(data);
  const [imageFile, setImageFile] = useState<File | null>(null);

  const getImageUrl = (data: ContentOption) => {
    return isFirstOption(data) ? data.imageUrl : data.thumbnailUrl;
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        if (isFirstOption(editData)) {
          setEditData({ ...editData, imageUrl: result });
        } else {
          setEditData({ ...editData, thumbnailUrl: result });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    try {
      let updatedData = { ...editData };

      if (imageFile) {
        const path = `onboarding/${type}/${editData.id}`;
        const url = await uploadFileToStorage(path, imageFile);

        if (isFirstOption(updatedData)) {
          updatedData = { ...updatedData, imageUrl: url };
        } else {
          updatedData = { ...updatedData, thumbnailUrl: url };
        }
      }

      onSave(updatedData);
      onClose();
    } catch (error) {
      console.error("Error saving:", error);
      alert("Error saving changes");
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          minWidth: 400,
        }}
      >
        <Typography variant="h6" gutterBottom>
          Edit {type === "first" ? "Platform" : "Content"}
        </Typography>
        <TextField
          fullWidth
          label="Title"
          value={editData.title}
          onChange={(e) =>
            setEditData((prev) => ({ ...prev, title: e.target.value }))
          }
          margin="normal"
        />
        <TextField
          fullWidth
          label="Order"
          type="number"
          value={editData.order}
          onChange={(e) =>
            setEditData((prev) => ({ ...prev, order: Number(e.target.value) }))
          }
          margin="normal"
        />
        r
        <Box sx={{ my: 2 }}>
          <input
            accept="image/*"
            type="file"
            onChange={handleImageChange}
            style={{ display: "none" }}
            id="image-upload"
          />
          <label htmlFor="image-upload">
            <Button variant="contained" component="span">
              Upload Image
            </Button>
          </label>
          {getImageUrl(editData) && (
            <Box sx={{ mt: 2 }}>
              <img
                src={getImageUrl(editData)}
                alt="Preview"
                style={{ maxWidth: "200px", maxHeight: "200px" }}
              />
            </Box>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Typography>Active</Typography>
          <Switch
            checked={editData.isActive}
            onChange={(e) =>
              setEditData((prev) => ({ ...prev, isActive: e.target.checked }))
            }
          />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export const OnboardingEditor: React.FC = () => {
  const [data, setData] = useState<OnboardingAcquisition | null>(null);
  const [selectedFirstOption, setSelectedFirstOption] = useState<string | null>(
    null
  );
  const [editModalData, setEditModalData] = useState<{
    open: boolean;
    data: ContentOption | null;
    type: ContentType;
  }>({
    open: false,
    data: null,
    type: "first",
  });
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addModalType, setAddModalType] = useState<"first" | "second">("first");
  const [hasChanges, setHasChanges] = useState(false);

  const handleAdd = async (title: string, imageFile: File) => {
    try {
      const newId = crypto.randomUUID();
      let path, url;

      if (addModalType === "first") {
        path = `onboarding/first/${newId}`;
        url = await uploadFileToStorage(path, imageFile);

        const newPlatform: FirstOption = {
          id: newId,
          title,
          imageUrl: url,
          order: data?.options.first.length || 0,
          isActive: true,
        };

        setData((prev) => ({
          ...prev!,
          options: {
            ...prev!.options,
            first: [...prev!.options.first, newPlatform],
          },
        }));
      } else {
        if (!selectedFirstOption) return;

        path = `onboarding/second/${newId}`;
        url = await uploadFileToStorage(path, imageFile);

        const newContent: SecondOption = {
          id: newId,
          firstOptionId: selectedFirstOption,
          title,
          thumbnailUrl: url,
          order:
            data?.options.second.filter(
              (item) => item.firstOptionId === selectedFirstOption
            ).length || 0,
          isActive: true,
        };

        setData((prev) => ({
          ...prev!,
          options: {
            ...prev!.options,
            second: [...prev!.options.second, newContent],
          },
        }));
      }
      setHasChanges(true);
    } catch (error) {
      console.error("Error adding new item:", error);
      alert("Error adding new item");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const docRef = doc(db, "OnboardingAcquisition", "onboarding_config_v1");
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setData(docSnap.data() as OnboardingAcquisition);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleSave = async () => {
    if (!data) return;

    try {
      const docRef = doc(db, "OnboardingAcquisition", "onboarding_config_v1");
      await updateDoc(docRef, {
        version: data.version,
        lastUpdated: data.lastUpdated,
        "options.first": data.options.first,
        "options.second": data.options.second,
      });
      alert("Successfully saved!");
      setHasChanges(false); // 저장 완료 후 변경사항 초기화
    } catch (error) {
      console.error("Error saving data:", error);
      alert("Error saving data");
    }
  };

  const handleEdit = (item: ContentOption, type: ContentType) => {
    setEditModalData({
      open: true,
      data: item,
      type,
    });
  };

  const handleEditSave = (updatedData: ContentOption) => {
    if (!data) return;

    if (isFirstOption(updatedData)) {
      setData({
        ...data,
        options: {
          ...data.options,
          first: data.options.first.map((item) =>
            item.id === updatedData.id ? updatedData : item
          ),
        },
      });
    } else {
      setData({
        ...data,
        options: {
          ...data.options,
          second: data.options.second.map((item) =>
            item.id === updatedData.id ? updatedData : item
          ),
        },
      });
    }
    setHasChanges(true); // 변경사항 있음을 표시
  };

  if (!data) return <div>Loading...</div>;

  return (
    <Box
      sx={{ display: "flex", gap: 2, p: 3, maxWidth: 1200, margin: "0 auto" }}
    >
      {/* First Options Column */}
      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h5">Platforms</Typography>
          <Button
            variant="contained"
            onClick={() => {
              setAddModalType("first");
              setAddModalOpen(true);
            }}
          >
            Add Platform
          </Button>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {data.options.first.map((item) => (
            <Card
              key={item.id}
              sx={{
                cursor: "pointer",
                bgcolor:
                  selectedFirstOption === item.id
                    ? "action.selected"
                    : "background.paper",
              }}
              onClick={() => setSelectedFirstOption(item.id)}
            >
              <CardContent
                sx={{ display: "flex", alignItems: "center", gap: 2 }}
              >
                <img
                  src={item.imageUrl}
                  alt={item.title}
                  style={{ width: 60, height: 60, objectFit: "cover" }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6">{item.title}</Typography>
                  <Typography color="textSecondary">
                    Order: {item.order} |{" "}
                    {item.isActive ? "Active" : "Inactive"}
                  </Typography>
                </Box>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(item, "first");
                  }}
                >
                  Edit
                </Button>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>

      {/* Second Options Column */}
      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h5">Content</Typography>
          <Button
            variant="contained"
            onClick={() => {
              setAddModalType("second");
              setAddModalOpen(true);
            }}
            disabled={!selectedFirstOption}
          >
            Add Content
          </Button>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {data.options.second
            .filter((item) =>
              selectedFirstOption
                ? item.firstOptionId === selectedFirstOption
                : true
            )
            .map((item) => (
              <Card key={item.id}>
                <CardContent
                  sx={{ display: "flex", alignItems: "center", gap: 2 }}
                >
                  <img
                    src={item.thumbnailUrl}
                    alt={item.title}
                    style={{ width: 60, height: 60, objectFit: "cover" }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6">{item.title}</Typography>
                    <Typography color="textSecondary">
                      Order: {item.order} |{" "}
                      {item.isActive ? "Active" : "Inactive"}
                    </Typography>
                  </Box>
                  <Button onClick={() => handleEdit(item, "second")}>
                    Edit
                  </Button>
                </CardContent>
              </Card>
            ))}
        </Box>
      </Box>

      {/* Save Button */}
      {hasChanges && (
        <Box
          sx={{
            position: "fixed",
            bottom: 80,
            right: 20,
            bgcolor: "warning.light",
            p: 2,
            borderRadius: 1,
          }}
        >
          <Typography color="warning.contrastText">
            변경사항이 있습니다. 저장하려면 아래 버튼을 클릭하세요!
          </Typography>
        </Box>
      )}
      <Button
        variant="contained"
        onClick={handleSave}
        sx={{ position: "fixed", bottom: 20, right: 20 }}
      >
        Save All Changes
      </Button>

      <OnboardingAddModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onAdd={handleAdd}
        type={addModalType}
      />

      {/* Edit Modal */}
      {editModalData.open && editModalData.data && (
        <EditModal
          open={editModalData.open}
          onClose={() => setEditModalData({ ...editModalData, open: false })}
          data={editModalData.data}
          onSave={handleEditSave}
          type={editModalData.type}
        />
      )}
    </Box>
  );
};

export default OnboardingEditor;
